import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AppShell } from './components/AppShell.tsx';
import { useSession } from './state/session.tsx';
import { Admin } from './routes/Admin.tsx';
import { Auth } from './routes/Auth.tsx';
import { Dashboard } from './routes/Dashboard.tsx';
import { DocumentList } from './routes/DocumentList.tsx';
import { Parties } from './routes/Parties.tsx';
import type { DocumentType } from './lib/types.ts';

// The editor and the report screens are the heaviest routes and are not needed
// on first paint, so they load on demand.
const DocumentEditor = lazy(() =>
  import('./routes/DocumentEditor.tsx').then((m) => ({ default: m.DocumentEditor })),
);
const DocumentDetail = lazy(() =>
  import('./routes/DocumentDetail.tsx').then((m) => ({ default: m.DocumentDetail })),
);
const PartyDetail = lazy(() =>
  import('./routes/PartyDetail.tsx').then((m) => ({ default: m.PartyDetail })),
);
const Items = lazy(() => import('./routes/Items.tsx').then((m) => ({ default: m.Items })));
const Expenses = lazy(() => import('./routes/Expenses.tsx').then((m) => ({ default: m.Expenses })));
const Payments = lazy(() => import('./routes/Payments.tsx').then((m) => ({ default: m.Payments })));
const Reports = lazy(() => import('./routes/Reports.tsx').then((m) => ({ default: m.Reports })));
const Settings = lazy(() => import('./routes/Settings.tsx').then((m) => ({ default: m.Settings })));
const NewBusiness = lazy(() =>
  import('./routes/NewBusiness.tsx').then((m) => ({ default: m.NewBusiness })),
);
const SharedDocument = lazy(() =>
  import('./routes/SharedDocument.tsx').then((m) => ({ default: m.SharedDocument })),
);
const Join = lazy(() => import('./routes/Join.tsx').then((m) => ({ default: m.Join })));
const Verify = lazy(() => import('./routes/Verify.tsx').then((m) => ({ default: m.Verify })));

/** Every document list is the same screen with a different type bound to it. */
const DOCUMENT_ROUTES: { path: string; type: DocumentType }[] = [
  { path: '/invoices', type: 'invoice' },
  { path: '/quotations', type: 'quotation' },
  { path: '/proformas', type: 'proforma' },
  { path: '/credit-notes', type: 'credit_note' },
  { path: '/purchase-orders', type: 'purchase_order' },
  { path: '/delivery-challans', type: 'delivery_challan' },
];

export function App() {
  const { status } = useSession();
  const location = useLocation();

  // Three routes have to render for a signed-out visitor: a shared document,
  // an invitation, and an email confirmation. They sit outside the auth gate
  // entirely — bouncing an invitee to a sign-in wall loses the token.
  const isPublicRoute =
    location.pathname.startsWith('/share/') ||
    location.pathname === '/join' ||
    location.pathname === '/verify';

  if (isPublicRoute) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/share/:token" element={<SharedDocument />} />
          <Route path="/join" element={<Join />} />
          <Route path="/verify" element={<Verify />} />
        </Routes>
      </Suspense>
    );
  }

  if (status === 'loading') return <BootScreen />;
  if (status === 'signed-out') return <Auth />;

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Outside AppShell on purpose. Running Vela is a different job from
            using it, and the company sidebar on an operator screen invites
            exactly the confusion of acting on the wrong account. */}
        <Route path="/admin" element={<Admin />} />

        <Route element={<AppShell />}>
          <Route index element={<Dashboard />} />

          {DOCUMENT_ROUTES.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<DocumentList type={route.type} />}
            />
          ))}

          <Route path="/documents/new" element={<DocumentEditor />} />
          <Route path="/documents/:id/edit" element={<DocumentEditor />} />
          <Route path="/documents/:id" element={<DocumentDetail />} />

          <Route path="/customers" element={<Parties kind="customer" />} />
          <Route path="/vendors" element={<Parties kind="vendor" />} />
          <Route path="/parties/:id" element={<PartyDetail />} />

          <Route path="/items" element={<Items />} />
          <Route path="/expenses" element={<Expenses />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/reports/:tab" element={<Reports />} />

          <Route path="/settings/businesses/new" element={<NewBusiness />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/:tab" element={<Settings />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function BootScreen() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        gap: 'var(--space-3)',
      }}
    >
      <p className="display" style={{ fontSize: 'var(--text-xl)', opacity: 0.35 }}>
        Vela
      </p>
    </div>
  );
}

function RouteFallback() {
  return (
    <div style={{ padding: 'var(--space-6)' }} aria-busy="true">
      <div className="skeleton" style={{ height: '2rem', width: '14rem', marginBottom: '1.5rem' }} />
      <div className="skeleton" style={{ height: '18rem', borderRadius: 'var(--radius-lg)' }} />
    </div>
  );
}
